import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "@/components/motion/Reveal";
import { STOCK, ASSETS } from "@/data/content";
import { useI18n } from "@/i18n";
import { SectionCta } from "@/components/SectionCta";
import { Wifi, Coffee, Armchair, TreePine } from "lucide-react";

const ICONS = [Wifi, Armchair, Coffee, TreePine];

export default function Workation() {
  const { t } = useI18n();
  const w = t.workation;
  const ITEMS = w.items.map((it, i) => ({ ...it, icon: ICONS[i] }));
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "12%"]);

  return (
    <section
      id="workation"
      data-testid="workation-section"
      ref={ref}
      className="relative bg-canvas-surface text-ink overflow-hidden"
    >
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20 grid grid-cols-12 gap-8 md:gap-16">
        <div className="col-span-12 md:col-span-6">
          <Reveal>
            <p className="overline mb-6">{w.overline}</p>
            <h2 className="serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-ink">
              {w.headingA} <span className="italic text-brand-red/80">{w.headingB}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 text-ink-muted text-base md:text-lg leading-relaxed max-w-md">
              {w.intro}
            </p>
          </Reveal>
        </div>

        <div className="col-span-12 md:col-span-6 md:pt-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-ink/10 border border-ink/10">
            {ITEMS.map(({ icon: Icon, title, desc }) => (
              <Reveal
                key={title}
                className="p-8 bg-canvas hover:bg-canvas-soft transition-colors duration-500"
              >
                <Icon strokeWidth={1} className="w-8 h-8 text-brand-red mb-5" />
                <h3 className="serif text-2xl font-light mb-2 text-ink">
                  {title}
                </h3>
                <p className="text-ink-muted text-sm leading-relaxed">{desc}</p>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Remote-work photos — 2x2 grid */}
        <div className="col-span-12 mt-6 md:mt-16">
          <Reveal>
            <div className="grid grid-cols-2 gap-4 md:gap-6">
              {[
                { src: ASSETS.workationOutdoor, alt: "Praca zdalna na leżaku z widokiem na dolinę Wisły" },
                { src: ASSETS.workationDesk, alt: "Wieczorna praca zdalna przy biurku w domku" },
                { src: ASSETS.workationMatcha, alt: "Przerwa na matchę i borówki z widokiem na Wisłę" },
                { src: ASSETS.cottageLiving, alt: "Miejsce do pracy — biurko i krzesło przy oknie" },
              ].map((img) => (
                <div
                  key={img.alt}
                  className="aspect-[4/3] overflow-hidden group"
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out"
                  />
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        {/* CTA — co robić po pracy */}
        <div className="col-span-12 mt-12 md:mt-16">
          <Reveal className="block">
            <div className="border-t border-ink/10 pt-10 md:pt-12 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
              <p className="serif text-2xl md:text-3xl font-light text-ink max-w-lg leading-snug">
                {w.ctaTitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-6 sm:gap-10">
                <SectionCta to="/domek" testid="workation-cta-domek">
                  {w.ctaDomek}
                </SectionCta>
                <SectionCta to="/okolica" testid="workation-cta-bydgoszcz">
                  {w.ctaBydgoszcz}
                </SectionCta>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
